import { useCallback } from 'react';
import { useSoundEnabled } from '@/context/SoundContext';
import { type SoundType } from '@/lib/soundConfig';
import {
  playTwoTone,
  playTritone,
  playChime,
  playErrorTone,
  playSuccessTone,
} from '@/lib/synthesizer';

/**
 * Hook to play sounds using iPad-style Web Audio synthesis
 * Immediate playback with no loading delays
 * Automatically uses .ogg files when available
 */
export function usePlaySounds() {
  const { soundEnabled } = useSoundEnabled();

  const playSound = useCallback(
    (soundType: SoundType) => {
      if (!soundEnabled) return;

      // Use Web Audio API synthesis for immediate playback
      // Frequencies and durations tuned for iPad aesthetic
      switch (soundType) {
        case 'add':
          // Two-tone: major third (very recognizable)
          playTwoTone(523, 659, 0.12);
          break;
        case 'decrease':
          // Two-tone: lower registers
          playTwoTone(440, 554, 0.1);
          break;
        case 'favorite':
          // Two-tone: bright and cheerful
          playTwoTone(587, 740, 0.13);
          break;
        case 'removeFavorite':
          // Descending tone: removal/undo
          playErrorTone(554, 440, 0.12);
          break;
        case 'success':
          // Rising tone: confirmation
          playSuccessTone(523, 784, 0.25);
          break;
        case 'orderCreated':
          // Play order created sound from audio file
          const audio = new Audio('/sounds/order-created.mp3');
          audio.play().catch(err => console.warn('[useSoundPlayer] Failed to play order-created.mp3:', err));
          break;
        case 'error':
          // Descending tone: error alert
          playErrorTone(659, 440, 0.2);
          break;
        default:
          console.warn(`[usePlaySounds] Unknown sound type: ${soundType}`);
      }
    },
    [soundEnabled]
  );

  return playSound;
}

/**
 * Legacy hook for backwards compatibility - now delegates to usePlaySounds
 */
export function usePlayBeep() {
  const playSound = usePlaySounds();

  return useCallback(
    (type: 'add' | 'decrease' = 'add') => {
      playSound(type);
    },
    [playSound]
  );
}
